import {
  Delete,
  PaymentsOutlined,
  Undo,
  Visibility,
} from '@mui/icons-material';
import {
  createReversalRoute,
  createReversalViewRoute,
} from 'adminConstants/routes';
import { RolePermission } from 'further-types/admin-user-role';
import DateRangePicker from 'further-ui/components/DateRangePicker';
import SearchField from 'further-ui/components/SearchField';
import Table, { type Column } from 'further-ui/components/Table';
import { ActionStatusLabels } from 'further-ui/labels';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import useListRealisations from './useListRealisations';
import PageFilters, { Filter } from 'components/new/PageFilters';
import {
  createLpRealisationViewRoute,
  LP_REALISATION_CREATE,
} from 'adminConstants/routes';
import Page from 'components/new/Page';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { useLayoutContext } from 'contexts/LayoutContext';
import AvatarWithName from 'components/new/AvatarWithName';

const pageId = 'realisations';

const ListRealisations: React.FC = () => {
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const [canCreate, canDelete, canReverse] = useHasPermissions([
    RolePermission.LpCreateRealisation,
    RolePermission.LpDeleteRealisation,
    RolePermission.LpCreateReversal,
  ]);
  const {
    realisations,
    totalCount,
    isFetching,
    filters,
    handleChangeFilters,
    handleDeleteRealisation,
    handleCloseDeleteDialog,
    handleOpenDeleteDialog,
    pagination,
    isDeleteDialogOpen,
  } = useListRealisations(pageId, selectedVehicleId ?? '');

  const columns: Array<Column<(typeof realisations)[number]>> = [
    {
      label: 'Asset name',
      key: 'assetName',
      width: 175,
      render: (row) => (
        <AvatarWithName avatarSize="lg" name={row.assetName ?? ''} />
      ),
    },
    {
      label: 'Status',
      key: 'status',
      width: 100,
      render: (row) => ActionStatusLabels[row.status],
    },
    {
      label: 'Realisation date',
      key: 'ledgerDate',
      width: 170,
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      label: 'Units realised',
      key: 'numberOfUnitsRealised',
      width: 150,
      render: (row) => numberToDisplayString(row.numberOfUnitsRealised),
    },
    {
      label: 'Total Amount (TCY)',
      key: 'totalRealisationAmountTcy',
      width: 180,
      render: (row) =>
        numberToCurrencyString(row.totalRealisationAmountTcy, {
          currency: row.currencyCode,
        }),
    },
    {
      label: 'Total Amount (LCY)',
      key: 'totalRealisationAmountLcy',
      width: 180,
      render: (row) =>
        numberToCurrencyString(row.totalRealisationAmountLcy, {
          currency: vehicle?.baseCurrency,
        }),
    },
  ];

  return (
    <Page
      tabTitle="Realisations"
      withHeadingDivider
      withStickyHeader
      heading="Realisations"
      headingIcon={PaymentsOutlined}
      actions={[
        {
          label: 'Add realisation',
          link: LP_REALISATION_CREATE,
          hidden: !canCreate,
        },
      ]}
      spacing="lg"
    >
      <PageFilters justify="space-between">
        <Filter size="md">
          <SearchField
            placeholder="Search by asset name"
            value={filters.searchQuery}
            onSearch={(query) => {
              handleChangeFilters({ searchQuery: query });
            }}
            autoSearchOnDebounce
            allowEmptyQuery
            fullWidth
          />
        </Filter>
        <Filter size="sm">
          <Filter>
            <DateRangePicker
              dateRange={filters.dateRange}
              onDateRangeChange={(dateRange) => {
                handleChangeFilters({ dateRange });
              }}
              placeholder="Select date range"
            />
          </Filter>
        </Filter>
      </PageFilters>
      <Table
        columns={columns}
        rows={realisations}
        pagination={pagination}
        rowsTotalCount={totalCount}
        isLoading={isFetching}
        scrollable
        emptyMessage="No realisations found"
        onRowClickHref={({ id }) => createLpRealisationViewRoute(id)}
        actions={[
          {
            label: 'View',
            icon: Visibility,
            link: (row) => createLpRealisationViewRoute(row.id),
          },
          {
            label: (row) => (row.reversedAt ? 'View reversal' : 'Reverse'),
            icon: Undo,
            link: (row) =>
              row.reversedAt
                ? createReversalViewRoute(row.id, 'realisation')
                : createReversalRoute(row.id, 'realisation'),
            hidden: (row) => !row.reversedAt && !canReverse,
            disabled: (row) => !row.reversedAt && row.canModify,
            tooltip: (row) =>
              !row.reversedAt && row.canModify
                ? 'This action can be edited or deleted directly as it is not in a locked period.'
                : undefined,
          },
          {
            label: 'Delete realisation',
            icon: Delete,
            onClick: (row) => {
              handleOpenDeleteDialog(row.id);
            },
            hidden: !canDelete,
            disabled: (row) => !row.canModify,
            tooltip: (row) =>
              !row.canModify ? row.canModifyReason : undefined,
          },
        ]}
      />
      <ConfirmDialog
        onClose={handleCloseDeleteDialog}
        open={isDeleteDialogOpen}
        maxWidth="sm"
        title="Are you sure you wish to delete this realisation?"
        content="This cannot be undone and all information relating to the realisation will be lost. If you have draft equalisations they will also be deleted."
        actions={{
          cancel: { label: 'Go back', variant: 'outlined', color: 'primary' },
          confirm: {
            label: 'Delete realisation',
            onClick: handleDeleteRealisation,
            variant: 'contained',
          },
        }}
      />
    </Page>
  );
};

export default ListRealisations;
